import { APP_FEATURES, APP_ROUTES } from '../constants';
import { SESSION_STORAGE_KEY } from '../constant/auth.constant';
import { getSessionItemWithExpiry } from './utility';

/**
 * Centralized navigation route used after login (and by `forbiddenPage` and the
 * `email-link` flow). EVERY role — students, guardians, teachers, cashiers,
 * admins, super admins — now lands on the common, role-neutral Home page.
 *
 * The previous per-role / privilege-cascade routing is preserved verbatim in
 * {@link getPrivilegeBasedRoute} (currently unused) so this can be reverted
 * quickly: point this function back at `getPrivilegeBasedRoute()` to restore the
 * old privilege-based landing pages.
 *
 * @returns The route path to navigate to (always `APP_ROUTES.HOME`)
 */
export const getNavigationRoute = (): string => {
  return APP_ROUTES.HOME;
};

/**
 * PREVIOUS navigation logic based on user privileges — KEPT AS A DOCUMENTED
 * BACKUP. This routed users to the first accessible page based on their role and
 * features. It is intentionally retained (currently unused) so the pre-universal
 * `/home` behavior can be restored easily by wiring `getNavigationRoute` back to
 * this function.
 *
 * NOTE: ETL setup gating for Super Admins is handled by ETLGuard component,
 * NOT by this navigation helper. ETLGuard reads state.etl.setupComplete from Redux.
 *
 * Priority order (follows sidebar structure):
 * 1. VIEW_DASHBOARD → Dashboard (main homepage)
 * 2. CLASSVEEW_POST_MANAGEMENT → News Feed (ClassVeew admin)
 * 3. CLASSVEEW_ORGANIZATION_MANAGEMENT → Admin Organizations (ClassVeew admin)
 * 4. CLASSVEEW_STUDENT_MANAGEMENT → Admin Students (ClassVeew admin)
 * 5. CLASSVEEW_TEACHER_MANAGEMENT → Admin Teachers (ClassVeew admin)
 * 6. ORGANIZATION_MANAGEMENT → My Institute Profile
 * 7. STUDENT_MANAGEMENT → Student Management
 * 8. ATTENDANCE_MARKING → Attendance Tracking
 * 9. RE_ENROLLMENT_MANAGEMENT → Re-enrollment Management
 * 10. INVOICE_MANAGEMENT → Invoice Management (Finance - Cashier role)
 * 11. FEE_TEMPLATE_MANAGEMENT → Template Management (Finance)
 * 12. TEACHER_MANAGEMENT → Teacher Management
 * 13. ROLE_MANAGEMENT → Role Management
 * 14. USER_MANAGEMENT → User/Guardian/Admin Management
 * 15. EVENT_MANAGEMENT → Event Management
 * 16. ACADEMIC_YEAR_MANAGEMENT → Academic Year Management
 * 17. CURRICULUM_MANAGEMENT → Curriculum Management
 * 18. GRADE_MANAGEMENT → Grade Management
 * 19. CLASSROOM_MANAGEMENT → Classroom Management
 * 20. SUBJECT_MANAGEMENT → Subject Management
 * 21. REPORT_MANAGEMENT → Reports
 * 22. Fallback → Dashboard (may show 403 if no dashboard access)
 *
 * @returns The route path to navigate to
 */
export const getPrivilegeBasedRoute = (): string => {
  try {
    const storedPrivileges = getSessionItemWithExpiry(SESSION_STORAGE_KEY.PRIVILEGES);
    const privileges: string[] = storedPrivileges ? JSON.parse(storedPrivileges) : [];

    // 1. Check for Dashboard view privilege (main homepage)
    if (privileges.includes(APP_FEATURES.VIEW_DASHBOARD)) {
      return APP_ROUTES.DASHBOARD;
    }

    // 3. Check for ClassVeew Admin privileges
    if (privileges.includes(APP_FEATURES.CLASSVEEW_POST_MANAGEMENT)) {
      return APP_ROUTES.ADMIN_NEWS_FEED;
    }

    if (privileges.includes(APP_FEATURES.CLASSVEEW_ORGANIZATION_MANAGEMENT)) {
      return APP_ROUTES.ADMIN_ORGANIZATIONS;
    }

    if (privileges.includes(APP_FEATURES.CLASSVEEW_STUDENT_MANAGEMENT)) {
      return APP_ROUTES.ADMIN_STUDENTS;
    }

    if (privileges.includes(APP_FEATURES.CLASSVEEW_TEACHER_MANAGEMENT)) {
      return APP_ROUTES.ADMIN_TEACHERS;
    }

    // 4. Check for My Institute Profile
    if (privileges.includes(APP_FEATURES.ORGANIZATION_MANAGEMENT)) {
      return APP_ROUTES.MY_INSTITUTE;
    }

    // 5. Check for Student Management features
    if (privileges.includes(APP_FEATURES.STUDENT_MANAGEMENT)) {
      return APP_ROUTES.STUDENT;
    }

    if (privileges.includes(APP_FEATURES.ATTENDANCE_MARKING)) {
      return APP_ROUTES.ATTENDANCE;
    }

    if (privileges.includes(APP_FEATURES.RE_ENROLLMENT_MANAGEMENT)) {
      return APP_ROUTES.RE_ENROLLMENT_MANAGEMENT;
    }

    // 6. Check for Finance privileges (Cashier role - CRITICAL for this fix!)
    if (privileges.includes(APP_FEATURES.INVOICE_MANAGEMENT)) {
      return APP_ROUTES.INVOICE_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.VIEW_SESSION_HISTORY)) {
      return APP_ROUTES.SESSION_HISTORY;
    }

    if (privileges.includes(APP_FEATURES.FEE_TEMPLATE_MANAGEMENT)) {
      return APP_ROUTES.FEE_TEMPLATE;
    }

    // 7. Check for Staff & Users privileges
    if (privileges.includes(APP_FEATURES.TEACHER_MANAGEMENT)) {
      return APP_ROUTES.TEACHER_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.USER_ATTENDANCE)) {
      return APP_ROUTES.TEACHER_ATTENDANCE;
    }

    if (privileges.includes(APP_FEATURES.ROLE_MANAGEMENT)) {
      return APP_ROUTES.ROLE_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.USER_MANAGEMENT)) {
      return APP_ROUTES.GUARDIAN_MANAGEMENT; // First user management page
    }

    // 8. Check for Communication privileges
    if (privileges.includes(APP_FEATURES.EVENT_MANAGEMENT)) {
      return APP_ROUTES.EVENT_MANAGEMENT;
    }

    // 9. Check for Academic privileges
    if (privileges.includes(APP_FEATURES.ROSTER_MANAGEMENT)) {
      return APP_ROUTES.TIMETABLE_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.SYLLABUS_MANAGEMENT)) {
      return APP_ROUTES.SYLLABUS_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.EXAM_MANAGEMENT)) {
      return APP_ROUTES.EXAM_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.TEACHER_ASSIGNMENT_MANAGEMENT)) {
      return APP_ROUTES.ASSIGN_CLASSROOMS;
    }

    // 10. Check for Library privileges
    if (privileges.includes(APP_FEATURES.BOOK_MANAGEMENT)) {
      return APP_ROUTES.BOOK_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.BOOK_CIRCULATION)) {
      return APP_ROUTES.BOOK_CIRCULATION;
    }

    // 11. Check for Master Data privileges
    if (privileges.includes(APP_FEATURES.ATTENDANCE_CUTOFF_MANAGEMENT)) {
      return APP_ROUTES.OPERATIONAL_TIME_SETTINGS;
    }

    if (privileges.includes(APP_FEATURES.CURRICULUM_MANAGEMENT)) {
      return APP_ROUTES.CURRICULUM_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.ACADEMIC_YEAR_MANAGEMENT)) {
      return APP_ROUTES.ACADEMIC_YEAR_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.GRADE_MANAGEMENT)) {
      return APP_ROUTES.GRADE_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.CLASSROOM_MANAGEMENT)) {
      return APP_ROUTES.CLASSROOM_MANAGEMENT;
    }

    if (privileges.includes(APP_FEATURES.SUBJECT_MANAGEMENT)) {
      return APP_ROUTES.SUBJECT_MANAGEMENT;
    }

    // 12. Check for Reports privileges
    if (privileges.includes(APP_FEATURES.REPORT_MANAGEMENT)) {
      return APP_ROUTES.REPORTS;
    }

    if (privileges.includes(APP_FEATURES.FINANCE_REPORT_MANAGEMENT)) {
      return APP_ROUTES.REPORTS_FINANCE;
    }

    if (privileges.includes(APP_FEATURES.STUDENT_REPORT_MANAGEMENT)) {
      return APP_ROUTES.REPORTS_STUDENT;
    }

    if (privileges.includes(APP_FEATURES.TEACHER_REPORT_MANAGEMENT)) {
      return APP_ROUTES.REPORTS_TEACHER;
    }

    if (privileges.includes(APP_FEATURES.STUDENT_ATTENDANCE_REPORT_MANAGEMENT)) {
      return APP_ROUTES.REPORTS_STUDENT_ATTENDANCE;
    }

    // 13. Check for Billing (last resort for users with only billing access)
    if (privileges.includes(APP_FEATURES.BILLING_MANAGEMENT)) {
      return APP_ROUTES.BILLING_AND_PLANS;
    }

    // Fallback for authenticated users without specific privileges
    // This may show 403 if user has no dashboard access
    return APP_ROUTES.DASHBOARD;
  } catch (error) {
    console.error('Error parsing privileges:', error);
    return APP_ROUTES.DASHBOARD;
  }
};

/**
 * Returns the appropriate home route considering authentication state.
 *
 * - If the user has a valid access token → returns privilege-based route
 * - If no access token → returns '/login'
 *
 * Use this in error boundaries and global error handlers where the user's
 * auth state is uncertain.
 *
 * @returns The route path to navigate to
 */
export const getHomeRoute = (): string => {
  const accessToken = getSessionItemWithExpiry(SESSION_STORAGE_KEY.ACCESS_TOKEN);

  if (!accessToken) {
    return '/login';
  }

  return getNavigationRoute();
};
